import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AudioDataResult, AudioResult } from "interfaces/sessions";
import { SessionV2 } from "interfaces/db";

// Redux-safe version of SessionV2 with serializable dates
export type ReduxSafeSessionV2 = Omit<SessionV2, 'started_at'> & {
  started_at: string; // ISO string
};

// Redux-safe version of AudioResult with serializable dates
export type ReduxSafeAudioResult = Omit<AudioResult, 'start' | 'end'> & {
  start: string; // ISO string
  end: string; // ISO string
};

// Redux-safe version of AudioDataResult
export type ReduxSafeAudioDataResult = {
  session: ReduxSafeSessionV2;
  audio: ReduxSafeAudioResult[];
  audio_links: string[];
};

// Helper functions to convert between Redux-safe and original types
export function toReduxSafeAudioDataResult(data: AudioDataResult): ReduxSafeAudioDataResult {
  return {
    session: {
      ...data.session,
      started_at: data.session.started_at instanceof Date ? data.session.started_at.toISOString() : 
                  (typeof data.session.started_at === 'string' ? data.session.started_at : new Date().toISOString())
    },
    audio: data.audio.map(a => ({
      ...a,
      start: a.start instanceof Date ? a.start.toISOString() : 
             (typeof a.start === 'string' ? a.start : new Date().toISOString()),
      end: a.end instanceof Date ? a.end.toISOString() : 
           (typeof a.end === 'string' ? a.end : new Date().toISOString())
    })),
    audio_links: data.audio_links
  };
}

export function fromReduxSafeAudioDataResult(data: ReduxSafeAudioDataResult): AudioDataResult {
  return {
    session: {
      ...data.session,
      started_at: new Date(data.session.started_at)
    },
    audio: data.audio.map(a => ({
      ...a,
      start: new Date(a.start),
      end: new Date(a.end)
    })),
    audio_links: data.audio_links
  };
}

export interface SessionAudioState {
  sessionIdToAudio: {
    [sessionId: string]: {
      data: ReduxSafeAudioDataResult | null;
      isLoading: boolean;
      error: string | null;
      // Download states
      isDownloading: boolean;
      downloadError: string | null;
      downloadSuccess: boolean;
    }
  }
}

const initialState: SessionAudioState = {
  sessionIdToAudio: {}
};

export const sessionAudioSlice = createSlice({
  name: 'sessionAudio',
  initialState,
  reducers: {
    startLoading: (state, action: PayloadAction<string>) => {
      const sessionId = action.payload;
      state.sessionIdToAudio[sessionId] = {
        ...state.sessionIdToAudio[sessionId],
        isLoading: true,
        error: null
      };
    },
    stopLoading: (state, action: PayloadAction<string>) => {
      const sessionId = action.payload;
      state.sessionIdToAudio[sessionId] = {
        ...state.sessionIdToAudio[sessionId],
        isLoading: false
      };
    },
    loadSuccess: (state, action: PayloadAction<{ sessionId: string; data: ReduxSafeAudioDataResult }>) => {
      const { sessionId, data } = action.payload;
      state.sessionIdToAudio[sessionId] = {
        data,
        isLoading: false,
        error: null,
        isDownloading: false,
        downloadError: null,
        downloadSuccess: false,
      };
    },
    loadError: (state, action: PayloadAction<{ sessionId: string; error: string }>) => {
      const { sessionId, error } = action.payload;
      state.sessionIdToAudio[sessionId] = {
        ...state.sessionIdToAudio[sessionId],
        isLoading: false,
        error,
        isDownloading: false,
        downloadError: null,
        downloadSuccess: false,
      };
    },
    clearAudioData: (state, action: PayloadAction<string>) => {
      const sessionId = action.payload;
      delete state.sessionIdToAudio[sessionId];
    },
    // Download actions
    startDownload: (state, action: PayloadAction<string>) => {
      const sessionId = action.payload;
      if (!state.sessionIdToAudio[sessionId]) {
        state.sessionIdToAudio[sessionId] = {
          data: null,
          isLoading: false,
          error: null,
          isDownloading: false,
          downloadError: null,
          downloadSuccess: false,
        };
      }
      state.sessionIdToAudio[sessionId].isDownloading = true;
      state.sessionIdToAudio[sessionId].downloadError = null;
      state.sessionIdToAudio[sessionId].downloadSuccess = false;
    },
    downloadSuccess: (state, action: PayloadAction<string>) => {
      const sessionId = action.payload;
      if (state.sessionIdToAudio[sessionId]) {
        state.sessionIdToAudio[sessionId].isDownloading = false;
        state.sessionIdToAudio[sessionId].downloadSuccess = true;
        state.sessionIdToAudio[sessionId].downloadError = null;
      }
    },
    downloadError: (state, action: PayloadAction<{ sessionId: string; error: string }>) => {
      const { sessionId, error } = action.payload;
      if (state.sessionIdToAudio[sessionId]) {
        state.sessionIdToAudio[sessionId].isDownloading = false;
        state.sessionIdToAudio[sessionId].downloadError = error;
        state.sessionIdToAudio[sessionId].downloadSuccess = false;
      }
    },
    clearDownloadState: (state, action: PayloadAction<string>) => {
      const sessionId = action.payload;
      if (state.sessionIdToAudio[sessionId]) {
        state.sessionIdToAudio[sessionId].isDownloading = false;
        state.sessionIdToAudio[sessionId].downloadError = null;
        state.sessionIdToAudio[sessionId].downloadSuccess = false;
      }
    },
  }
})

export const { 
  startLoading, 
  stopLoading,
  loadSuccess, 
  loadError,
  clearAudioData,
  startDownload,
  downloadSuccess,
  downloadError,
  clearDownloadState,
} = sessionAudioSlice.actions;

export default sessionAudioSlice.reducer; 